import { apiFetch } from './client'

export type WeeklyCostsRow = {
  id: number
  company_id: number
  week_start: string
  payroll: number
  subs: number
  materials: number
  other: number
  fuel?: number
  credit_cards?: number
  source?: string
  carried_forward?: boolean
}

export type WeeklyCostsResponse = {
  week_start: string
  current: WeeklyCostsRow | null
  prefill: WeeklyCostsRow | null
  proposal_id?: number | null
  forecast?: WeekForecast | null
}

export async function fetchWeeklyCosts(week?: string): Promise<WeeklyCostsResponse> {
  const qs = week ? `?week=${encodeURIComponent(week)}` : ''
  const json = await apiFetch<{ data: WeeklyCostsResponse }>('GET', `/api/weekly-costs${qs}`)
  return json.data
}

export type WeeklyCostProposal = {
  id?: number
  payroll: number
  subs: number
  materials: number
  other: number
  fuel?: number
  credit_cards?: number
  inferred?: string[]
}

export type WeekForecastAction = {
  kind: string
  title: string
  subtitle: string
  cta: string | null
  url: string | null
}

export type WeekForecast = {
  likely_in: string
  likely_in_note?: string
  going_out: string
  going_out_breakdown?: string
  verdict_state: 'short' | 'covered' | 'unknown'
  verdict_label: string
  verdict_sentence: string
  verdict_bg?: string
  verdict_fg?: string
  reminder_url: string | null
  primary_action?: WeekForecastAction | null
}

export type WeeklyCostPrompt = {
  week_start: string
  weekday: string
  has_row: boolean
  dismissed: boolean
  show: boolean
  proposal: WeeklyCostProposal | null
  last_week: WeeklyCostProposal | null
  pending_reply: (WeeklyCostProposal & { id: number; excerpt: string | null }) | null
}

export async function fetchWeeklyCostPrompt(week?: string): Promise<WeeklyCostPrompt> {
  const qs = week ? `?week=${encodeURIComponent(week)}` : ''
  const json = await apiFetch<{ data: WeeklyCostPrompt }>('GET', `/api/weekly-costs/prompt${qs}`)
  return json.data
}

export async function proposeWeeklyCosts(body: {
  week_start?: string
  text?: string
  payroll?: number
  subs?: number
  materials?: number
  other?: number
  fuel?: number
  credit_cards?: number
}): Promise<WeeklyCostProposal & { id: number }> {
  const json = await apiFetch<{ data: WeeklyCostProposal & { id: number } }>('POST', '/api/weekly-costs/propose', body)
  return json.data
}

export async function confirmWeeklyCosts(proposalId: number): Promise<WeeklyCostsRow & { forecast?: WeekForecast }> {
  const json = await apiFetch<{ data: WeeklyCostsRow & { forecast?: WeekForecast } }>('POST', '/api/weekly-costs/confirm', { proposal_id: proposalId })
  return json.data
}

export async function saveSameAsLastWeek(weekStart: string): Promise<{ id: number; created: boolean; forecast: WeekForecast }> {
  const json = await apiFetch<{ data: { id: number; created: boolean; forecast: WeekForecast } }>('POST', '/api/weekly-costs/carry', { week_start: weekStart })
  return json.data
}

export async function dismissWeeklyCostPrompt(weekStart: string): Promise<void> {
  await apiFetch('POST', '/api/weekly-costs/dismiss', { week_start: weekStart })
}

export async function saveWeeklyCosts(body: {
  week_start?: string
  payroll: number
  subs: number
  materials: number
  other: number
  source?: 'typed' | 'voice'
}): Promise<WeeklyCostsRow & { forecast?: WeekForecast }> {
  const json = await apiFetch<{ data: WeeklyCostsRow & { forecast?: WeekForecast } }>('POST', '/api/weekly-costs', body)
  return json.data
}
